import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import InfinityCard from "./InfinityCard";

const infinityAreas = ["Health", "Defense", "Attack", "Accuracy", "Turn Loss"];
const turnsActiveOptions = [
  "Instant",
  "1 Turn",
  "2 Turns",
  "3 Turns",
  "Instant + 1 Turn",
  "2 Turns / 1 Turn",
];

const EditCardModal = ({ open, onClose, card, onUpdate }) => {
  // Local state to edit the card; initialize from the passed card prop.
  const [localCard, setLocalCard] = useState({
    name: "",
    description: "",
    effect: "",
    areaAffected: [],
    turnsActive: "",
    max: "",
    finite: false,
    craftNeeded: "",
    craftAmount: "",
    availForPurchase: false,
    price: "",
    season: "",
    imageUrl: "",
    bgColor1: "#ffffff",
    bgColor2: "#ffffff",
  });

  useEffect(() => {
    if (card) {
      setLocalCard({
        name: card.name || "",
        description: card.description || "",
        effect: card.effect || "",
        areaAffected: card.areaAffected || [],
        turnsActive: card.turnsActive || "",
        max: card.max || "",
        finite: card.finite || false,
        craftNeeded: card.craftNeeded || "",
        craftAmount: card.craftAmount || "",
        availForPurchase: card.availForPurchase || false,
        price: card.price || "",
        season: card.season || "",
        imageUrl: card.imageUrl || "",
        bgColor1: card.bgColor1 || "#ffffff",
        bgColor2: card.bgColor2 || "#ffffff",
      });
    }
  }, [card]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Card</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" height="70vh">
          {/* Left Section: Editable Fields */}
          <Box
            width="40%"
            bgcolor="white"
            p={2}
            display="flex"
            flexDirection="column"
            overflow="auto"
          >
            <TextField
              label="Name"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.name}
              onChange={(e) =>
                setLocalCard({ ...localCard, name: e.target.value })
              }
            />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.description}
              onChange={(e) =>
                setLocalCard({ ...localCard, description: e.target.value })
              }
            />
            <TextField
              label="Effect"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.effect}
              onChange={(e) =>
                setLocalCard({ ...localCard, effect: e.target.value })
              }
            />
            <FormControl
              fullWidth
              margin="dense"
              size="small"
              variant="outlined"
            >
              <InputLabel id="edit-area-affected-label">
                Area Affected
              </InputLabel>
              <Select
                labelId="edit-area-affected-label"
                multiple
                value={localCard.areaAffected}
                onChange={(e) => {
                  const {
                    target: { value },
                  } = e;
                  setLocalCard({
                    ...localCard,
                    areaAffected:
                      typeof value === "string" ? value.split(",") : value,
                  });
                }}
                input={<OutlinedInput label="Area Affected" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {infinityAreas.map((area) => (
                  <MenuItem key={area} value={area}>
                    <Checkbox
                      checked={localCard.areaAffected.indexOf(area) > -1}
                    />
                    <ListItemText primary={area} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              margin="dense"
              size="small"
              variant="outlined"
            >
              <InputLabel id="edit-turns-active-label">Turns Active</InputLabel>
              <Select
                labelId="edit-turns-active-label"
                value={localCard.turnsActive}
                onChange={(e) =>
                  setLocalCard({ ...localCard, turnsActive: e.target.value })
                }
                label="Turns Active"
              >
                {turnsActiveOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Max"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              type="number"
              value={localCard.max}
              onChange={(e) =>
                setLocalCard({ ...localCard, max: e.target.value })
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={localCard.finite}
                  onChange={(e) =>
                    setLocalCard({ ...localCard, finite: e.target.checked })
                  }
                  size="small"
                />
              }
              label="Finite"
            />
            <TextField
              label="Craft Needed"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.craftNeeded}
              onChange={(e) =>
                setLocalCard({ ...localCard, craftNeeded: e.target.value })
              }
            />
            <TextField
              label="Craft Amount"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              type="number"
              value={localCard.craftAmount}
              onChange={(e) =>
                setLocalCard({ ...localCard, craftAmount: e.target.value })
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={localCard.availForPurchase}
                  onChange={(e) =>
                    setLocalCard({
                      ...localCard,
                      availForPurchase: e.target.checked,
                    })
                  }
                  size="small"
                />
              }
              label="Available for Purchase"
            />
            <TextField
              label="Price"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              type="number"
              value={localCard.price}
              onChange={(e) =>
                setLocalCard({ ...localCard, price: e.target.value })
              }
            />
            <TextField
              label="Season"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              type="number"
              value={localCard.season}
              onChange={(e) =>
                setLocalCard({ ...localCard, season: e.target.value })
              }
            />
            <TextField
              label="Image URL"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.imageUrl}
              onChange={(e) =>
                setLocalCard({ ...localCard, imageUrl: e.target.value })
              }
            />
            <Box display="flex" gap="8px" mt={1}>
              <input
                type="color"
                value={localCard.bgColor1}
                onChange={(e) =>
                  setLocalCard({ ...localCard, bgColor1: e.target.value })
                }
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
              <input
                type="color"
                value={localCard.bgColor2}
                onChange={(e) =>
                  setLocalCard({ ...localCard, bgColor2: e.target.value })
                }
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
            </Box>
          </Box>
          {/* Right Section: Preview */}
          <Box
            width="60%"
            bgcolor="lightgray"
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="auto"
          >
            <InfinityCard
              name={localCard.name}
              description={localCard.description}
              effect={localCard.effect}
              areaAffected={localCard.areaAffected}
              turnsActive={localCard.turnsActive}
              max={localCard.max}
              finite={localCard.finite}
              craftNeeded={localCard.craftNeeded}
              craftAmount={localCard.craftAmount}
              forPurchase={localCard.availForPurchase}
              price={localCard.price}
              season={localCard.season}
              bgColor1={localCard.bgColor1}
              bgColor2={localCard.bgColor2}
              imageUrl={localCard.imageUrl}
              heightPer={80}
              borderPx={10}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onUpdate(localCard);
            onClose();
          }}
          variant="contained"
          color="primary"
          size="small"
        >
          Save
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          size="small"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCardModal;
